import React, { useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Users, Award, TrendingUp, Heart, Zap, BookOpen, Target, CheckCircle, Briefcase } from 'lucide-react';

const CareersPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const coreValues = [
    {
      icon: Award,
      title: "Excellence",
      description: "We pursue the highest standards in everything we do, from client service to professional development."
    },
    {
      icon: Heart,
      title: "Integrity",
      description: "Transparency, honesty, and ethical conduct form the foundation of our practice and relationships."
    },
    {
      icon: Users,
      title: "Collaboration",
      description: "We believe great outcomes emerge when diverse minds work together toward common goals."
    },
    {
      icon: Zap,
      title: "Innovation",
      description: "We embrace technology and new approaches to solve complex challenges in accounting and compliance."
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: "Professional Growth",
      description: "Structured career progression with mentorship from experienced CAs and industry exposure across diverse sectors."
    },
    {
      icon: BookOpen,
      title: "Continuous Learning",
      description: "Regular training on latest tax laws, AI/ML compliance, international standards, and technology tools."
    },
    {
      icon: Target,
      title: "Diverse Clientele",
      description: "Work with startups, tech companies, NRIs, and multinational corporations across various industries."
    },
    {
      icon: Briefcase,
      title: "Work-Life Balance",
      description: "Flexible working arrangements, supportive culture, and respect for personal time and well-being."
    }
  ];

  const openings = [
    {
      id: 1,
      title: "Chartered Accountant - Tax & Compliance",
      experience: "2-5 years",
      type: "Full-time",
      location: "Bangalore",
      description: "Handle direct and indirect tax compliance, GST filings, and advisory for tech startups and SMEs."
    },
    {
      id: 2,
      title: "CA Associate - AI/ML Compliance",
      experience: "1-3 years",
      type: "Full-time",
      location: "Bangalore",
      description: "Emerging role focused on AI regulatory compliance, data privacy consulting, and tech audits."
    },
    {
      id: 3,
      title: "Senior Accountant",
      experience: "3-6 years",
      type: "Full-time",
      location: "Bangalore",
      description: "Manage bookkeeping, financial statements, and accounting operations for multiple clients."
    },
    {
      id: 4,
      title: "Audit Associate",
      experience: "0-2 years",
      type: "Full-time / Articleship",
      location: "Bangalore",
      description: "Support statutory audits, internal audits, and compliance reviews. Perfect for CA finalists."
    }
  ];

  const faqs = [
    {
      question: "What is the work culture like at RST & Co.?",
      answer: "We foster a collaborative, learning-focused environment where every team member's contribution is valued. We balance professional excellence with personal well-being, offering flexibility and mentorship."
    },
    {
      question: "Do you offer opportunities for freshers and CA finalists?",
      answer: "Yes! We welcome CA finalists for articleship and fresh CAs for associate positions. We provide structured training and hands-on experience across diverse service areas."
    },
    {
      question: "What kind of clients will I work with?",
      answer: "You'll work with a diverse portfolio including tech startups, AI/ML companies, NRI clients, foreign subsidiaries, manufacturing firms, and established businesses across sectors."
    },
    {
      question: "How does RST & Co. support professional development?",
      answer: "We provide regular internal training sessions, sponsor external certifications, encourage participation in industry seminars, and offer mentorship from senior partners."
    },
    {
      question: "Is there scope for specialization?",
      answer: "Absolutely. Based on your interests and aptitude, you can specialize in areas like AI compliance, NRI taxation, transfer pricing, startup advisory, or audit and assurance."
    }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.pexels.com/photos/7414216/pexels-photo-7414216.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Team collaboration"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="max-w-3xl">
            <div className="inline-block bg-brand-blue/20 border border-brand-blue/50 px-4 py-2 mb-8">
              <span className="font-mono text-xs tracking-widest text-brand-blue uppercase">Careers at RST & Co.</span>
            </div>
            
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[1.05] mb-4 tracking-tight">
              Shape Your Future in
              <br />
              <span className="text-brand-blue">Accounting & Finance</span>
            </h1>
            
            <div className="text-left mb-8">
              <p className="font-heading text-2xl font-bold text-white mb-1">ಆರ್ ಎಸ್ ಟಿ ಅಂಡ್ ಕಂ</p>
              <p className="font-heading text-lg text-gray-300">ಸನ್ನದು ಲೆಕ್ಕ ಪರಿಶೋಧಕರು</p>
            </div>
            
            <p className="font-body text-xl text-gray-300 leading-relaxed mb-12 max-w-2xl">
              Join a forward-thinking CA firm where innovation meets tradition. Build expertise in AI compliance, NRI taxation, and startup advisory while working with diverse clients across industries.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => scrollToSection('openings')}
                className="bg-brand-blue hover:bg-brand-blue-hover text-white font-medium px-8 py-6 h-auto text-base transition-all duration-300 group"
                data-testid="careers-view-openings"
              >
                <span>View Open Positions</span>
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} strokeWidth={2} />
              </Button>
              <Button 
                onClick={() => scrollToSection('culture')}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-medium px-8 py-6 h-auto text-base transition-all duration-300"
              >
                Learn About Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section id="culture" className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="mb-16">
            <div className="inline-block bg-brand-blue/10 px-4 py-1.5 rounded-md mb-6">
              <span className="font-mono text-xs tracking-widest text-brand-blue uppercase">Why RST & Co.</span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              Why Leading Professionals Choose Us
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
              We're not just another CA firm. We're building the future of accounting with a focus on emerging areas like AI compliance and tech-driven solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {benefits.map((benefit, idx) => {
              const IconComponent = benefit.icon;
              return (
                <div key={idx} className="bg-gray-50 border border-gray-200 p-8 hover:border-brand-blue/50 hover:shadow-lg transition-all duration-300">
                  <div className="w-14 h-14 bg-brand-blue/10 flex items-center justify-center mb-6">
                    <IconComponent className="text-brand-blue" size={28} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-gray-900 mb-3 tracking-tight">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Core Values */}
          <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 p-12">
            <h3 className="font-heading text-3xl font-bold text-gray-900 mb-12 text-center">
              Our Core Values
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {coreValues.map((value, idx) => {
                const IconComponent = value.icon;
                return (
                  <div key={idx} className="text-center">
                    <div className="w-16 h-16 bg-white border-2 border-brand-blue flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="text-brand-blue" size={32} strokeWidth={1.5} />
                    </div>
                    <h4 className="font-heading text-lg font-bold text-gray-900 mb-2">
                      {value.title}
                    </h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section id="openings" className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="mb-16">
            <div className="inline-block bg-brand-blue/10 px-4 py-1.5 rounded-md mb-6">
              <span className="font-mono text-xs tracking-widest text-brand-blue uppercase">Current Openings</span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              Open Positions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
              Explore opportunities to grow your career with a dynamic team serving innovative clients.
            </p>
          </div>

          <div className="space-y-6">
            {openings.map((job) => (
              <div key={job.id} className="bg-white border border-gray-200 p-8 hover:border-brand-blue/50 hover:shadow-lg transition-all duration-300">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                  <div>
                    <h3 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                      {job.title}
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      <span className="font-mono text-xs bg-gray-100 px-3 py-1 text-gray-700">
                        {job.experience}
                      </span>
                      <span className="font-mono text-xs bg-brand-blue/10 px-3 py-1 text-brand-blue">
                        {job.type}
                      </span>
                      <span className="font-mono text-xs bg-gray-100 px-3 py-1 text-gray-700">
                        📍 {job.location}
                      </span>
                    </div>
                  </div>
                  <Button 
                    className="bg-brand-blue hover:bg-brand-blue-hover text-white font-medium px-6 py-3 transition-all duration-300 whitespace-nowrap"
                    onClick={() => scrollToSection('apply')}
                  >
                    Apply Now
                  </Button>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  {job.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Apply */}
      <section id="apply" className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              Ready to Join Our Team?
            </h2>
            <p className="text-lg text-gray-600 mb-12 leading-relaxed">
              Send your resume and cover letter to <a href="mailto:careers@rstandco.co.in" className="text-brand-blue font-semibold hover:underline">careers@rstandco.co.in</a> with the position title in the subject line.
            </p>
            
            <div className="bg-gray-50 border border-gray-200 p-8 text-left">
              <h3 className="font-heading text-xl font-bold text-gray-900 mb-4">
                What to Include:
              </h3>
              <ul className="space-y-3">
                {[
                  "Updated resume/CV with educational qualifications and work experience",
                  "Cover letter explaining your interest in the role and RST & Co.",
                  "Copies of relevant certifications (CA, CS, CMA, etc.)",
                  "References or recommendation letters (if available)"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="text-brand-blue flex-shrink-0 mt-0.5" size={20} strokeWidth={2} />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="max-w-3xl mx-auto">
            <div className="mb-12 text-center">
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                Frequently Asked Questions
              </h2>
            </div>
            
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-white border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                    className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-heading text-lg font-bold text-gray-900 pr-4">
                      {faq.question}
                    </span>
                    <ArrowRight 
                      className={`text-brand-blue flex-shrink-0 transition-transform duration-300 ${
                        expandedFaq === idx ? 'rotate-90' : ''
                      }`} 
                      size={20} 
                      strokeWidth={2}
                    />
                  </button>
                  {expandedFaq === idx && (
                    <div className="px-8 pb-6">
                      <p className="text-gray-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-[1400px] mx-auto px-[7.6923%] text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
            Build Your Career With Us
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto leading-relaxed">
            Take the next step in your professional journey. Join RST & Co. and grow with India's emerging leader in tech-forward accounting services.
          </p>
          <Button 
            onClick={() => window.location.href = 'mailto:careers@rstandco.co.in'}
            className="bg-brand-blue hover:bg-brand-blue-hover text-white font-medium px-8 py-6 h-auto text-base transition-all duration-300 group"
          >
            <span>Send Your Application</span>
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} strokeWidth={2} />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
